// client/src/components/PublicProfile/PublicMatchHistory.js
import React, { useState, useEffect } from 'react';
import { 
  Trophy, Target, Users, Calendar, Activity, AlertTriangle, 
  ChevronLeft, ChevronRight, TrendingUp, Award, Star, RotateCcw 
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const PublicMatchHistory = ({ profileData, t }) => {
  const [matches, setMatches] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadMatchHistory(currentPage);
  }, [currentPage, profileData]);

  const loadMatchHistory = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(
        `/api/public-profile/${profileData.username}/matches?page=${page}&limit=10`
      );
      
      setMatches(response.data.matches);
      setStatistics(response.data.statistics);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to load match history:', error);
      setError(error.response?.data?.message || 'Failed to load match history');
      toast.error('Failed to load match history');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (pagination?.totalPages || 1)) {
      setCurrentPage(newPage);
    }
  };

  const getGameModeDisplayName = (gameMode) => {
    const gameModeNames = {
      'teamDeathmatch': 'Team Deathmatch',
      'domination': 'Domination',
      'searchAndDestroy': 'Search & Destroy',
      'hardpoint': 'Hardpoint',
      'killConfirmed': 'Kill Confirmed',
      'captureTheFlag': 'Capture The Flag',
      'frontline': 'Frontline',
      'freeForAll': 'Free For All',
      'gunGame': 'Gun Game',
      'sticksAndStones': 'Sticks & Stones',
      'oneShotOneKill': 'One Shot One Kill',
      'cranked': 'Cranked',
      'rapidFire': 'Rapid Fire',
      'attackOfTheUndead': 'Attack of the Undead',
      'battleRoyale': 'Battle Royale',
      'quickSolo': 'Quick Solo',
      'searchAndRescue': 'Search & Rescue'
    };
    return gameModeNames[gameMode] || gameMode;
  };

  const getUserDisplayName = (userObj) => {
    if (typeof userObj === 'string') return userObj;
    return userObj?.inGameName || userObj?.username || userObj?.name || 'Unknown';
  };

  // Helper function to get all participants (including fillers)
  const getAllParticipants = (match) => {
    const participants = [];
    
    // Check if we have a participantsList (some APIs return this)
    if (match.participantsList && Array.isArray(match.participantsList)) {
      participants.push(...match.participantsList.map(p => ({
        ...p,
        type: 'real',
        displayName: getUserDisplayName(p),
        isCurrentUser: (typeof p === 'string' ? p : p._id || p.id) === profileData._id,
        isWinner: match.winners?.some(w => {
          const winnerId = typeof w === 'string' ? w : w._id || w.id;
          const participantId = typeof p === 'string' ? p : p._id || p.id;
          return winnerId === participantId;
        })
      })));
    }
    // Add real participants
    else if (match.participants && Array.isArray(match.participants)) {
      participants.push(...match.participants.map(p => ({
        ...p,
        type: 'real',
        displayName: getUserDisplayName(p),
        isCurrentUser: (typeof p === 'string' ? p : p._id || p.id) === profileData._id,
        isWinner: match.winners?.some(w => {
          const winnerId = typeof w === 'string' ? w : w._id || w.id;
          const participantId = typeof p === 'string' ? p : p._id || p.id;
          return winnerId === participantId;
        })
      })));
    }
    // If no participants array, reconstruct from available data
    else {
      // Add winners as participants
      if (match.winners && Array.isArray(match.winners)) {
        participants.push(...match.winners.map(w => ({
          ...w,
          type: 'real',
          displayName: getUserDisplayName(w),
          isCurrentUser: (typeof w === 'string' ? w : w._id || w.id) === profileData._id,
          isWinner: true
        })));
      }
      
      // Add the profile owner if not already included
      const profileAlreadyIncluded = participants.some(p => {
        const pId = typeof p === 'string' ? p : p._id || p.id;
        return pId === profileData._id;
      });
      
      if (!profileAlreadyIncluded) {
        participants.push({
          _id: profileData._id,
          type: 'real',
          displayName: profileData.inGameName || profileData.username,
          isCurrentUser: true,
          isWinner: match.isWinner
        });
      }
      
      // Add creator if not already included
      if (match.creator) {
        const creatorId = typeof match.creator === 'string' ? match.creator : match.creator._id || match.creator.id;
        const creatorAlreadyIncluded = participants.some(p => {
          const pId = typeof p === 'string' ? p : p._id || p.id;
          return pId === creatorId;
        });
        if (!creatorAlreadyIncluded) {
          participants.push({
            ...match.creator,
            type: 'real',
            displayName: getUserDisplayName(match.creator),
            isCurrentUser: creatorId === profileData._id,
            isWinner: match.winners?.some(w => {
              const winnerId = typeof w === 'string' ? w : w._id || w.id;
              return winnerId === creatorId;
            })
          });
        }
      }
      
      // Fill remaining slots based on participantCount
      const participantCount = match.participantCount || match.maxPlayers || 0;
      const remainingSlots = participantCount - participants.length;
      for (let i = 0; i < remainingSlots; i++) {
        participants.push({
          _id: `player-${i}`,
          type: 'real',
          displayName: 'Player',
          isCurrentUser: false,
          isWinner: false
        });
      }
    }
    
    // Add fillers if available
    if (match.fillers && Array.isArray(match.fillers)) {
      participants.push(...match.fillers.map(f => ({
        ...f,
        type: 'filler',
        displayName: f.name || 'Filler',
        isCurrentUser: false,
        isWinner: match.winners?.some(w => {
          const winnerId = typeof w === 'string' ? w : w._id || w.id;
          return winnerId === f._id;
        })
      })));
    }
    
    return participants;
  };

  const getTimeSince = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 60) return `${diffMins}${t('mins', 'm')}`;
    if (diffHours < 24) return `${diffHours}${t('hrs', 'h')}`;
    return `${diffDays}${t('daysShort', 'd')}`;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="tournament-list-header">
          <div className="tournament-list-header-content">
            <h2 className="tournament-list-title">
              <Calendar className="w-5 h-5 text-yellow-500" />
              {t('matchHistory', 'MATCH HISTORY')}
            </h2>
          </div>
        </div>
        
        <div className="tournament-list-container">
          <div className="tournament-list-content">
            <div className="tournament-empty-state">
              <div className="loading-spinner"></div>
              <p className="tournament-empty-title">{t('loadingMatches', 'Loading matches...')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="tournament-list-header">
          <div className="tournament-list-header-content">
            <h2 className="tournament-list-title">
              <Calendar className="w-5 h-5 text-yellow-500" />
              {t('matchHistory', 'MATCH HISTORY')}
            </h2>
          </div>
        </div>
        
        <div className="tournament-list-container">
          <div className="tournament-list-content">
            <div className="tournament-empty-state">
              <AlertTriangle className="w-12 h-12 text-red-400" />
              <p className="tournament-empty-title text-red-400">{t('errorLoadingHistory', 'Error loading match history')}</p>
              <p className="tournament-empty-subtitle">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="tournament-list-header">
        <div className="tournament-list-header-content">
          <h2 className="tournament-list-title">
            <Calendar className="w-5 h-5 text-yellow-500" />
            {t('matchHistory', 'MATCH HISTORY')}
          </h2>
          
          <button 
            onClick={() => { 
              loadMatchHistory(currentPage); 
              toast.success(t('historyRefreshed', 'History refreshed!')); 
            }}
            className="tournament-refresh-btn"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            {t('refresh', 'REFRESH')}
          </button>
        </div>
      </div>

      {/* Main Container */}
      <div className="tournament-list-container">
        <div className="tournament-list-content">
          {/* Statistics */}
          {statistics && (
            <div className="mb-3 p-2 bg-gray-800/40 rounded border border-gray-700">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 text-blue-400 font-bold text-xs">
                  <TrendingUp className="w-3 h-3" />
                  {t('combatStats', 'STATS')}:
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="tournament-status-base" style={{ backgroundColor: 'rgba(107, 114, 128, 0.2)', borderColor: '#6b7280', color: '#d1d5db', minWidth: 'auto', padding: '2px 6px' }}>
                    {statistics.totalMatches} {t('total', 'Total')}
                  </div>
                  <div className="tournament-status-base" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)', borderColor: '#22c55e', color: '#86efac', minWidth: 'auto', padding: '2px 6px' }}>
                    {statistics.wins} {t('wins', 'W')}
                  </div>
                  <div className="tournament-status-base" style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', borderColor: '#ef4444', color: '#fca5a5', minWidth: 'auto', padding: '2px 6px' }}>
                    {statistics.totalMatches - statistics.wins} {t('losses', 'L')}
                  </div>
                  <div className="tournament-status-base" style={{ backgroundColor: 'rgba(59, 130, 246, 0.2)', borderColor: '#3b82f6', color: '#60a5fa', minWidth: 'auto', padding: '2px 6px' }}>
                    {statistics.winRate}% {t('winRate', 'WR')}
                  </div>
                  <div className="tournament-status-base" style={{ backgroundColor: 'rgba(251, 191, 36, 0.2)', borderColor: '#fbbf24', color: '#fde68a', minWidth: 'auto', padding: '2px 6px' }}>
                    {statistics.totalPrizeWon} {t('totalPrize', 'Prize')}
                  </div>
                  <div className="tournament-status-base" style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)', borderColor: '#8b5cf6', color: '#c4b5fd', minWidth: 'auto', padding: '2px 6px' }}>
                    {statistics.averagePrize} {t('avgPrize', 'Avg')}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Match List */}
          {matches.length === 0 ? (
            <div className="tournament-empty-state">
              <Trophy className="w-12 h-12 text-gray-600" />
              <p className="tournament-empty-title">{t('noMatches', 'NO MATCHES FOUND')}</p>
              <p className="tournament-empty-subtitle">{t('noMatchesDesc', 'This player hasn\'t completed any public matches yet.')}</p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {matches.map((match, index) => {
                  const allParticipants = getAllParticipants(match);
                  const isGiveaway = match.tournamentType === 'giveaway';
                  const isHighPrize = match.prizePool >= 500;
                  const creatorName = getUserDisplayName(match.creator);
                  const winnersCount = match.winners?.length || 1;
                  const prizeWon = match.isWinner ? Math.floor((match.prizePool || 0) / winnersCount) : 0;
                  
                  // Get card classes based on match type
                  const getCardClasses = () => {
                    const baseClasses = ['tournament-card-compact', 'tournament-card-base'];
                    
                    if (isGiveaway) {
                      baseClasses.push('tournament-card-giveaway');
                    } else if (isHighPrize) {
                      baseClasses.push('tournament-card-high-prize');
                    } else {
                      baseClasses.push('tournament-card-standard');
                    }
                    
                    return baseClasses.join(' ');
                  };
                  
                  return (
                    <div key={match._id} className={getCardClasses()}>
                      {/* Giveaway Badge */}
                      {isGiveaway && (
                        <div className="tournament-giveaway-badge">
                          <span>🎁</span>
                          <span>{t('giveaway', 'GIVEAWAY')}</span>
                        </div>
                      )}
                      
                      {/* Border effects */}
                      {isGiveaway && (
                        <>
                          <div className="tournament-border-glow-top"></div>
                          <div className="tournament-border-glow-bottom"></div>
                        </>
                      )}
                      {isHighPrize && !isGiveaway && (
                        <>
                          <div className="tournament-border-red-top"></div>
                          <div className="tournament-border-red-bottom"></div>
                        </>
                      )}

                      <div className="tournament-card-compact-main">
                        <div className="tournament-card-compact-left">
                          <div className="tournament-card-compact-title" 
                               style={{ color: isGiveaway ? '#fef3c7' : isHighPrize ? '#fecaca' : 'white' }}>
                            {creatorName}'s {getGameModeDisplayName(match.gameMode)}
                            {match.tournamentType === 'giveaway' && (
                              <span className="text-yellow-400 ml-2">🎁</span>
                            )}
                          </div>
                          
                          <div className="tournament-card-compact-details">
                            <span className="text-red-400">⚔️{getGameModeDisplayName(match.gameMode)}</span>
                            <span className="text-blue-400">📍{match.map}</span>
                            <span className="text-yellow-400">👥{allParticipants.length}</span>
                            {match.maxKills && (
                              <span className="text-pink-400">💀{match.maxKills}K</span>
                            )}
                            <span className={`font-bold ${
                              isGiveaway ? 'text-yellow-400 animate-pulse' : 
                              isHighPrize ? 'text-red-400' : 'text-green-400'
                            }`}>
                              🏆{match.prizePool}
                            </span>
                            <span className="tournament-card-compact-time">⏰{getTimeSince(match.completedAt)}</span>
                          </div>
                        </div>

                        <div className="tournament-card-compact-right">
                          <div className="tournament-card-compact-actions">
                            {/* Match Result Status */}
                            <div className={`tournament-status-base ${
                              match.isWinner 
                                ? isGiveaway ? 'tournament-status-completed-giveaway' : 'tournament-status-completed'
                                : 'tournament-status-cancelled'
                            }`}>
                              {match.isWinner ? (
                                <>
                                  <Trophy className="w-3 h-3" />
                                  {t('won', 'WON')}
                                </>
                              ) : (
                                <>
                                  <Activity className="w-3 h-3" />
                                  {t('lost', 'LOST')}
                                </>
                              )}
                            </div>

                            {/* Prize Won */}
                            {match.isWinner && prizeWon > 0 && (
                              <div className="tournament-status-base" style={{
                                backgroundColor: 'rgba(34, 197, 94, 0.2)',
                                borderColor: '#22c55e',
                                color: '#86efac'
                              }}>
                                +{prizeWon}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="tournament-card-compact-bottom">
                        <div className="tournament-card-compact-team">
                          <span className="tournament-card-compact-team-label">{t('combatants', 'COMBATANTS')}:</span>
                          
                          <div className="flex flex-wrap gap-1 mt-1">
                            {allParticipants.map((participant, participantIndex) => {
                              const participantId = typeof participant === 'string' ? participant : participant._id || participant.id;
                              
                              return (
                                <span key={participantId || participantIndex} 
                                      className={`tournament-participant-badge ${
                                        participant.isCurrentUser 
                                          ? 'tournament-participant-self' 
                                          : participant.isWinner
                                          ? isGiveaway ? 'tournament-participant-winner' : 'tournament-participant-winner-high-prize'
                                          : participant.type === 'filler'
                                          ? 'tournament-participant-filler'
                                          : 'tournament-participant-other'
                                      }`}>
                                  {participant.type === 'filler' ? (
                                    <>🤖 {participant.displayName}</>
                                  ) : participant.isWinner ? (
                                    <>👑 {participant.displayName}</>
                                  ) : (
                                    <>👤 {participant.displayName}</>
                                  )}
                                  {participant.isCurrentUser && (
                                    <span className="ml-1">
                                      <Star className="w-3 h-3 inline" />
                                      ({t('thisPlayer', 'This Player')})
                                    </span>
                                  )}
                                </span>
                              );
                            })}
                          </div>
                        </div>

                        <div className="tournament-card-compact-info">
                          {match.password && <span className="text-yellow-500 font-bold">{t('passwordProtected', '🔒PWD')}</span>}
                          {isHighPrize && !isGiveaway && <span className="text-red-500 font-bold">{t('highStakes', '🔥HIGH STAKES')}</span>}
                          <span className="text-gray-400 text-xs">📅{new Date(match.completedAt).toLocaleDateString()}</span>
                        </div>
                      </div>

                      {/* Winners Section (if multiple winners) */}
                      {match.winners && match.winners.length > 1 && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <div className="text-sm text-gray-400 mb-1">
                            {t('winners', 'Winners')} ({match.winners.length}):
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {match.winners.map((winner, winnerIndex) => {
                              const winnerName = getUserDisplayName(winner);
                              const winnerId = typeof winner === 'string' ? winner : winner._id || winner.id;
                              const isCurrentUser = winnerId === profileData._id;
                              
                              return (
                                <span key={winnerId || winnerIndex} 
                                      className={`tournament-participant-badge tournament-participant-winner`}>
                                  <Trophy className="w-3 h-3" />
                                  {winnerName}{isCurrentUser && (
                                    <>
                                      <Star className="w-3 h-3 inline ml-1" />
                                      ({t('thisPlayer', 'This Player')})
                                    </>
                                  )}
                                </span>
                              );
                            })}
                          </div>
                          {winnersCount > 1 && match.isWinner && (
                            <div className="text-xs text-gray-400 mt-1">
                              {t('prizeShared', 'Prize shared between {count} winners').replace('{count}', winnersCount)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="pagination-container">
                  <div className="pagination-controls">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!pagination.hasPrevPage}
                      className="pagination-button pagination-nav"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      {t('previous', 'Previous')}
                    </button>

                    <span className="pagination-info">
                      {pagination.currentPage} / {pagination.totalPages}
                    </span>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                      className="pagination-button pagination-nav"
                    >
                      {t('next', 'Next')}
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="pagination-info">
                    {t('pageInfo', 'Showing {current} of {total} matches')
                      .replace('{current}', `${((currentPage - 1) * 10) + 1}-${Math.min(currentPage * 10, pagination.total)}`)
                      .replace('{total}', pagination.total)}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Summary */}
          {statistics && matches.length > 0 && (
            <div className="mt-4 p-4 bg-gray-800/30 rounded-lg border border-gray-700">
              <h4 className="flex items-center gap-2 text-lg font-semibold text-white mb-3">
                <TrendingUp className="w-4 h-4" />
                {t('matchSummary', 'Match Summary')}
              </h4>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center gap-3">
                  <Award className="w-4 h-4 text-blue-400" />
                  <div>
                    <div className="text-xl font-bold text-white">
                      {statistics.wins}/{statistics.totalMatches}
                    </div>
                    <div className="text-sm text-gray-400">
                      {t('winLossRecord', 'Win/Loss Record')}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Trophy className="w-4 h-4 text-yellow-400" />
                  <div>
                    <div className="text-xl font-bold text-white">
                      {statistics.totalPrizeWon.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-400">
                      {t('totalPrizeWon', 'Total Prize Won')}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Target className="w-4 h-4 text-green-400" />
                  <div>
                    <div className="text-xl font-bold text-white">
                      {statistics.averagePrize.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-400">
                      {t('averagePrize', 'Average Prize per Win')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicMatchHistory;